import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Trophy, ArrowRight, TrendingUp, Home } from 'lucide-react';
import { useCategories } from '../../../../hooks/useCategories';
import { useHabitStore } from '../../../../stores/habitStore';
import { useFolderStore } from '../../../../stores/folderStore';
import { useUserStats } from '../../../../hooks/useUserStats';
import { prayerLabel } from '../../../../utils/prayerBlocks';
import { formatPoints } from '../../../../utils/levelCalculation';
import { formatClock, formatMinutes } from '../../../../utils/time';
import { rampBarFill } from '../../../../utils/progressRamp';
import type { Routine, RoutineStep } from '../../../../types';

interface RoutineReportProps {
  routine: Routine;
  earnedXP: number;
  /** Actual elapsed time across the run (seconds) — not the routine's planned total. */
  timeSpentSeconds: number;
  /** Run XP broken down by domain (category id → XP); sums to earnedXP. */
  xpByDomain?: Record<string, number>;
  onClose: () => void;
}

// Reveal cascade stages: 0 total number → 1 step-domain rings → 2 main-domain ring → 3 user bar.
const S_NUMBER = 0;
const S_DOMAINS = 1;
const S_MAIN = 2;
const S_USER = 3;

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

interface DomainGroup {
  id: string;
  name: string;
  color: string;
  xp: number;
  progress: number;       // domain level progress 0–100 (real)
  level: number;
  steps: string[];        // completed step labels in this domain
  isMain: boolean;
}

/** A thin circular progress ring with centred content; fills when `reveal` flips true. */
function Ring({ progress, color, reveal, size = 76, stroke = 7, children }: {
  progress: number; color: string; reveal: boolean; size?: number; stroke?: number; children?: React.ReactNode;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.min(100, Math.max(0, progress));
  const offset = reveal ? c * (1 - pct / 100) : c;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="absolute inset-0 -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeOpacity={0.15} strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 850ms cubic-bezier(0.22,1,0.36,1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">{children}</div>
    </div>
  );
}

export function RoutineReport({ routine, earnedXP, timeSpentSeconds, xpByDomain = {}, onClose }: RoutineReportProps) {
  const [showContent, setShowContent] = useState(false);
  const [stage, setStage] = useState(-1);
  const [displayedPoints, setDisplayedPoints] = useState(0);
  const [domainsT, setDomainsT] = useState(0); // 0→1 step-domain number count-up
  const [mainT, setMainT] = useState(0);        // 0→1 main-domain number count-up
  const [userPct, setUserPct] = useState(0);
  const { getCategoryColor, getCategoryName } = useCategories();
  const { getActiveHabits } = useHabitStore();
  const { getFolderById } = useFolderStore();
  const { stats } = useUserStats();
  const habits = getActiveHabits();
  const categoryColor = getCategoryColor(routine.category);

  const stepLabel = (step: RoutineStep): string => {
    if (step.type === 'habit') {
      const base = habits.find(h => h.id === step.habitId)?.name ?? 'Habit';
      return step.slot ? `${base} · ${step.slot}` : base;
    }
    if (step.type === 'folder') return getFolderById(step.folderId ?? '')?.name ?? 'Folder';
    if (step.type === 'prayer') return step.prayerKey ? prayerLabel(step.prayerKey) : 'Prayer';
    return 'Break';
  };
  const stepDomain = (step: RoutineStep): string => {
    if (step.type === 'habit') return habits.find(h => h.id === step.habitId)?.category ?? routine.category;
    if (step.type === 'folder') return getFolderById(step.folderId ?? '')?.category ?? routine.category;
    if (step.type === 'prayer') return 'existential';
    return routine.category; // break → main domain
  };

  // Build domain groups (merges "XP by domain" + "Completed Steps"). The routine's
  // main domain is always present (it collects break XP) and rendered last.
  const { stepGroups, mainGroup } = useMemo(() => {
    const domainProgress = (id: string) => stats.categories.find(c => c.id === id);
    const map = new Map<string, DomainGroup>();
    const ensure = (id: string): DomainGroup => {
      let g = map.get(id);
      if (!g) {
        const d = domainProgress(id);
        g = { id, name: getCategoryName(id), color: getCategoryColor(id), xp: xpByDomain[id] ?? 0,
              progress: d?.progress ?? 0, level: d?.level ?? 0, steps: [], isMain: id === routine.category };
        map.set(id, g);
      }
      return g;
    };
    for (const step of routine.steps) ensure(stepDomain(step)).steps.push(stepLabel(step));
    // Any domain that earned XP but somehow had no step (defensive).
    for (const id of Object.keys(xpByDomain)) ensure(id);
    ensure(routine.category); // main always present

    const all = [...map.values()];
    return {
      stepGroups: all.filter(g => !g.isMain).sort((a, b) => b.xp - a.xp),
      mainGroup: all.find(g => g.isMain)!,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routine, xpByDomain, stats.categories]);

  const userPctTarget = Math.min(100, Math.max(0, stats.progress));

  // --- animation plumbing ---
  const rafs = useRef<number[]>([]);
  const timers = useRef<number[]>([]);
  useEffect(() => () => {
    rafs.current.forEach(r => cancelAnimationFrame(r));
    timers.current.forEach(t => clearTimeout(t));
  }, []);
  const animate = (from: number, to: number, dur: number, set: (v: number) => void) => {
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      set(from + (to - from) * easeOut(t));
      if (t < 1) rafs.current.push(requestAnimationFrame(tick));
    };
    rafs.current.push(requestAnimationFrame(tick));
  };

  useEffect(() => {
    const push = (fn: () => void, ms: number) => timers.current.push(window.setTimeout(fn, ms));
    push(() => setShowContent(true), 100);
    // Stage 0 — total number counts up.
    push(() => { setStage(S_NUMBER); animate(0, earnedXP, 750, (v) => setDisplayedPoints(Math.round(v))); }, 350);
    // Stage 1 — step-domain rings fill + their numbers count.
    push(() => { setStage(S_DOMAINS); animate(0, 1, 750, setDomainsT); }, 1250);
    // Stage 2 — main-domain ring appears beside + counts.
    push(() => { setStage(S_MAIN); animate(0, 1, 700, setMainT); }, 2050);
    // Stage 3 — user level bar fills.
    push(() => { setStage(S_USER); animate(0, userPctTarget, 850, setUserPct); }, 2800);
  }, [earnedXP, userPctTarget]);

  const renderGroup = (g: DomainGroup, t: number, reveal: boolean) => {
    const gained = Math.round(g.xp * t);
    return (
      <div
        key={g.id}
        className="flex flex-col items-center gap-2 rounded-xl border border-border p-3 transition-all duration-500"
        style={{
          opacity: reveal ? 1 : 0,
          transform: reveal ? 'translateY(0)' : 'translateY(8px)',
          backgroundColor: g.isMain ? `${g.color}0a` : undefined,
          borderColor: g.isMain ? `${g.color}40` : undefined,
          minWidth: 132,
        }}
      >
        <Ring progress={g.progress} color={g.color} reveal={reveal}>
          {g.xp > 0
            ? <span className="text-lg font-bold tabular-nums" style={{ color: g.color }}>+{formatPoints(gained)}</span>
            : <span className="text-sm font-semibold text-muted-foreground">Lv {g.level}</span>}
        </Ring>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-sm font-medium text-foreground">
            {g.isMain && <Home className="w-3.5 h-3.5" style={{ color: g.color }} />}
            {g.name}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">Lv {g.level} · {Math.round(g.progress)}%</div>
          {g.steps.length > 0 && (
            <p className="mt-1 max-w-[160px] text-[11px] leading-snug text-muted-foreground line-clamp-2">
              {g.steps.join(' · ')}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className={`bg-card rounded-2xl shadow-lg border border-border w-full transform transition-all duration-500
        ${showContent ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-4 opacity-0 scale-95'}`}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-6 border-b border-border"
          style={{
            backgroundColor: `color-mix(in srgb, ${categoryColor} 15%, transparent)`,
            borderBottom: `1px solid ${categoryColor}25`,
          }}
        >
          <div>
            <h2 className="text-xl font-bold mb-1" style={{ color: categoryColor }}>Routine Complete!</h2>
            <p className="text-sm text-muted-foreground">{routine.name}</p>
          </div>
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${categoryColor}15` }}>
            <Trophy className="w-6 h-6" style={{ color: categoryColor }} />
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Headline — total XP (animated number) + time. */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">XP Earned</p>
              <p className="mt-1 text-3xl font-bold tabular-nums" style={{ color: categoryColor }}>
                {formatPoints(displayedPoints)}
              </p>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Time Spent</p>
              <p className="mt-1 text-3xl font-bold tabular-nums text-foreground">{formatClock(timeSpentSeconds)}</p>
              <p className="text-xs text-muted-foreground mt-1">of {formatMinutes(routine.totalDuration)} planned</p>
            </div>
          </div>

          {/* Merged domains + steps — the cascade. */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground">Domains</h3>
            <div className="flex flex-wrap items-start gap-3">
              {stepGroups.map(g => renderGroup(g, domainsT, stage >= S_DOMAINS))}
              {renderGroup(mainGroup, mainT, stage >= S_MAIN)}
            </div>
          </div>

          {/* User level progress — fills last. */}
          <div className="rounded-lg border border-border p-4 transition-opacity duration-500" style={{ opacity: stage >= S_USER ? 1 : 0.35 }}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground">
                  Level {stats.level}
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">{stats.highestRank}</span>
                </span>
              </div>
              <span className="text-xs text-muted-foreground tabular-nums">{Math.round(userPct)}%</span>
            </div>
            {/* User progress follows the theme accent, not the routine's domain colour. */}
            <div className="relative h-2 rounded-full bg-muted overflow-hidden">
              <div className="absolute inset-y-0 left-0 rounded-full" style={rampBarFill(userPct)} />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {formatPoints(stats.pointsToNextLevel)} XP to Level {stats.level + 1}
            </p>
          </div>

          {/* Continue */}
          <button
            onClick={onClose}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 mt-2 rounded-md transition-colors"
            style={{ backgroundColor: `${categoryColor}15`, color: categoryColor }}
          >
            Continue
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
